import { useState } from 'react';
import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';
import { plans, type Plan } from '../../../mocks/plans';

type FilterType = 'ALL' | 'TOTO' | 'LIXIL' | 'オプション';

const gradeOrder = ['スタンダード', 'アドバンス', 'プレミアム', 'オプション'];

const gradeBadgeStyle = (grade: string) => {
  if (grade === 'プレミアム') return 'bg-amber-50 text-amber-700 border border-amber-200';
  if (grade === 'アドバンス') return 'bg-navy/10 text-navy border border-navy/20';
  if (grade === 'スタンダード') return 'bg-gray-100 text-gray-600 border border-gray-200';
  return 'bg-orange-50 text-accent border border-accent/20';
};

const optionIconMap: Record<number, string> = {
  7: 'ri-home-4-line',
  8: 'ri-building-line',
  9: 'ri-tools-line',
  10: 'ri-lightbulb-line',
  11: 'ri-windy-line',
  12: 'ri-shield-check-line',
};

function PlanCard({ plan, index, isVisible }: { plan: Plan; index: number; isVisible: boolean }) {
  const isOption = plan.brand === 'オプション';

  return (
    <div
      className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-accent/40 transition-all duration-300 hover:-translate-y-1 group cursor-pointer flex flex-col"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
        transition: `opacity 0.6s ease ${(index % 4) * 0.08}s, transform 0.6s ease ${(index % 4) * 0.08}s, border-color 0.3s`,
      }}
    >
      {isOption ? (
        <div className="relative bg-gradient-to-br from-navy/5 to-accent/5 flex flex-col items-center justify-center" style={{ height: '140px' }}>
          <div className="w-14 h-14 flex items-center justify-center bg-white rounded-full border border-gray-100 mb-2">
            <i className={`${optionIconMap[plan.id] ?? 'ri-settings-3-line'} text-2xl text-accent`}></i>
          </div>
          <span className="text-xs font-bold text-navy/60 tracking-widest uppercase">Option</span>
        </div>
      ) : (
        <div className="relative overflow-hidden bg-gray-50" style={{ height: '160px' }}>
          <img
            src={plan.image}
            alt={plan.name}
            className="w-full h-full object-contain object-center group-hover:scale-105 transition-transform duration-500"
          />
          {plan.badge && (
            <span className="absolute top-3 left-3 bg-accent text-white text-xs font-bold px-2.5 py-1 rounded-full">
              {plan.badge}
            </span>
          )}
          <span
            className={`absolute top-3 right-3 text-xs font-bold px-2.5 py-1 rounded-full ${
              plan.brand === 'TOTO' ? 'bg-navy/10 text-navy' : 'bg-emerald-100 text-emerald-700'
            }`}
          >
            {plan.brand}
          </span>
        </div>
      )}

      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-2">
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${gradeBadgeStyle(plan.grade)}`}>
            {plan.grade}
          </span>
          {isOption && (
            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-accent/10 text-accent border border-accent/20">
              オプション
            </span>
          )}
        </div>

        <h3 className="font-bold text-navy text-sm leading-snug mb-1">{plan.name}</h3>
        <p className="text-gray-400 text-xs mb-3 leading-relaxed">{plan.model}</p>

        <ul className="space-y-1 mb-4 flex-1">
          {plan.features.map((f) => (
            <li key={f} className="flex items-start gap-1.5 text-xs text-gray-500">
              <span className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-check-line text-accent text-xs"></i>
              </span>
              {f}
            </li>
          ))}
        </ul>

        {plan.links && plan.links.length > 0 && (
          <div className="border-t border-gray-100 pt-2.5 mt-auto mb-2">
            <p className="text-xs text-gray-400 mb-1.5 font-medium">メーカー公式サイト</p>
            <div className="flex flex-col gap-1">
              {plan.links.map((link) => (
                <a
                  key={link.url}
                  href={link.url}
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="flex items-center gap-1.5 text-xs text-navy/70 hover:text-accent transition-colors duration-200 group"
                >
                  <span className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-external-link-line text-xs group-hover:text-accent"></i>
                  </span>
                  <span className="underline underline-offset-2 decoration-gray-200 group-hover:decoration-accent leading-tight">
                    {link.label}
                  </span>
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="border-t border-gray-100 pt-3 mt-auto">
          <div className="flex items-end justify-between">
            <div>
              <span className="text-xs text-gray-400 block">コミコミ価格</span>
              <span className="font-display font-black text-accent text-xl leading-tight">
                ¥{plan.price.toLocaleString()}
                <span className="text-xs font-normal text-gray-400">（税抜）</span>
              </span>
            </div>
            <button
              onClick={() => {
                const el = document.querySelector('#contact');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-xs font-bold text-accent border border-accent/30 hover:bg-accent hover:text-white px-3 py-1.5 rounded-full whitespace-nowrap transition-all duration-300 cursor-pointer"
            >
              見積もり
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const filterLabels: { key: FilterType; label: string }[] = [
  { key: 'ALL', label: 'すべて' },
  { key: 'LIXIL', label: 'LIXIL' },
  { key: 'TOTO', label: 'TOTO' },
  { key: 'オプション', label: 'オプション工事' },
];

export default function PlansSection() {
  const { ref, isVisible } = useIntersectionObserver(0.05);
  const [filter, setFilter] = useState<FilterType>('ALL');

  const filteredPlans =
    filter === 'ALL' ? plans : plans.filter((p) => p.brand === filter);

  const sortedPlans = [...filteredPlans].sort(
    (a, b) => gradeOrder.indexOf(a.grade) - gradeOrder.indexOf(b.grade)
  );

  return (
    <section id="plans" className="py-20 md:py-28 bg-navy-50">
      <div
        ref={ref}
        className="max-w-7xl mx-auto px-4 md:px-8"
        style={{
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.7s ease',
        }}
      >
        <div className="text-center mb-12">
          <span className="inline-block text-accent font-display font-bold text-xs tracking-widest uppercase mb-3">
            Plans
          </span>
          <h2 className="text-3xl md:text-4xl font-black text-navy leading-tight mb-3">
            迷わせない、間違いない。<br />
            <span className="text-accent">厳選12プラン。</span>
          </h2>
          <div className="w-16 h-1 bg-accent mx-auto rounded-full mb-4"></div>
          <p className="text-gray-500 text-sm md:text-base max-w-xl mx-auto leading-relaxed">
            LIXILとTOTOのスタンダードからプレミアムまで。<br />
            すべて<strong className="text-navy">コミコミ価格</strong>で明朗会計。追加費用の心配は一切ありません。
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2 mb-8">
          {filterLabels.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 cursor-pointer ${
                filter === key
                  ? key === 'オプション'
                    ? 'bg-accent text-white'
                    : 'bg-navy text-white'
                  : 'bg-white text-navy border border-gray-200 hover:border-navy'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {filter === 'オプション' && (
          <div className="bg-accent/5 border border-accent/20 rounded-2xl p-4 mb-8 text-center">
            <p className="text-sm text-gray-600 leading-relaxed">
              <strong className="text-accent">オプション工事</strong>はトイレ本体プランと組み合わせてご依頼いただけます。<br className="hidden sm:block" />
              まとめてご依頼でさらにお得！LINEでお気軽にご相談ください。
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
          {sortedPlans.map((plan, index) => (
            <PlanCard key={plan.id} plan={plan} index={index} isVisible={isVisible} />
          ))}
        </div>

        <div className="mt-10 text-center">
          <p className="text-gray-500 text-sm mb-4">プランのご質問・詳細はお気軽にどうぞ。LINEで写真を送るだけで簡単見積もり！</p>
          <button
            onClick={() => {
              const el = document.querySelector('#contact');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-full font-bold text-base whitespace-nowrap transition-all duration-300 hover:-translate-y-0.5 cursor-pointer"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-line-fill text-lg"></i>
            </span>
            LINEで無料見積もりを依頼する
          </button>
        </div>
      </div>
    </section>
  );
}
